import * as React from 'react';
import { IFotoramaAPI, IFotoramaData, TFotoramaSlideIndex } from '../../../typings/fotorama';
import { GalleryView } from '../../components/GalleryView';
import { FullscreenControls } from '../../components/GalleryView/FullscreenControls';
import { FullscreenHeader } from '../../components/GalleryView/FullscreenHeader';
import { GalleryPreloader } from '../../components/GalleryView/GalleryPreloader';
import { loadFotorama } from '../../utils/gallery';
import { IGalleryOptions, IGalleryTab } from '../../types/gallery';
import { CallbackContainer } from '../CallbackContainer';
import { ECallbackWidgetPlacement } from '../../types/callbackWidget';

export interface IGalleryContainerProps {
  name: string;
  slides: IFotoramaData[];
  preloaderImageUrl?: string;
  galleryOptions: IGalleryOptions;
  tabs?: IGalleryTab[];
  activeSlide?: number;
  phoneButton: React.ReactNode;
  onTabClick?(tab: IGalleryTab): void;
  onSlideChange?(index: number): void;
  onFullscreenEnter?(): void;
  onFullscreenExit?(): void;
}

export interface IGalleryContainerState {
  currentIndex: number;
  isFullscreen: boolean;
  isReady: boolean;
}

export class GalleryContainer extends React.Component<IGalleryContainerProps, IGalleryContainerState> {
  public state = {
    currentIndex: 0,
    isFullscreen: false,
    isReady: false,
  };

  private $fotorama: {
    on(type: string, cb: (e: Event, fotorama: IFotoramaAPI) => void): void;
    data(name: string): IFotoramaAPI;
    unbind(): void;
    show(id: number): void;
  };

  private fotoramaAPI: IFotoramaAPI;

  public componentDidUpdate(prevProps: Readonly<IGalleryContainerProps>) {
    const { activeSlide } = this.props;

    if (activeSlide !== undefined && activeSlide !== prevProps.activeSlide) {
      this.fotoramaAPI.show(activeSlide);
    }
  }

  public componentWillUnmount() {
    this.$fotorama?.unbind();
  }

  public render() {
    const { isReady, isFullscreen } = this.state;
    const { slides, name, preloaderImageUrl, phoneButton, tabs, onTabClick } = this.props;

    const options = this.getOptions();

    return (
      <div>
        <GalleryView
          total={slides.length}
          options={options}
          tabs={tabs}
          onTabClick={onTabClick}
          onMount={this.onGalleryViewMount}
          onLeftArrowClick={this.goToSlide('<')}
          onRightArrowClick={this.goToSlide('>')}
          onFullscreenButtonClick={this.toggleFullscreen(true)}
          isHidden={!isReady}
        />
        {preloaderImageUrl && !isReady && <GalleryPreloader imageUrl={preloaderImageUrl} />}
        {isFullscreen && (
          <>
            <FullscreenHeader
              name={name}
              phoneButton={phoneButton}
              onClose={this.toggleFullscreen(false)}
              callbackButton={<CallbackContainer placement={ECallbackWidgetPlacement.Gallery} />}
            />
            {slides.length > 1 && <FullscreenControls onControlClick={direction => this.goToSlide(direction)()} />}
          </>
        )}
      </div>
    );
  }

  private goToSlide = (slide: TFotoramaSlideIndex) => () => {
    this.fotoramaAPI.show(slide);
  };

  private toggleFullscreen = (isFullscreen: boolean) => () => {
    if (isFullscreen) {
      this.fotoramaAPI.requestFullScreen();
    } else {
      this.fotoramaAPI.cancelFullScreen();
    }

    this.setState({ isFullscreen });
  };

  private onGalleryViewMount = async (gallery: HTMLDivElement) => {
    await loadFotorama();

    if (window.$) {
      this.initFotorama(gallery);
    }
  };

  private initFotorama(gallery: HTMLDivElement) {
    const { slides } = this.props;

    this.$fotorama = window
      .$(gallery)
      .on('fotorama:fullscreenenter fotorama:fullscreenexit', this.onToggleFullscreen)
      .on('fotorama:ready', this.onReady)
      .fotorama({
        data: slides,
        startindex: 0,
      });

    this.$fotorama.on('fotorama:show', this.onSlideChange);

    this.fotoramaAPI = this.$fotorama.data('fotorama');
  }

  private onSlideChange = () => {
    const { onSlideChange } = this.props;
    const { activeIndex } = this.fotoramaAPI;

    this.setState({
      currentIndex: this.fotoramaAPI.activeIndex,
    });

    if (onSlideChange) {
      onSlideChange(activeIndex);
    }
  };

  private onReady = () => {
    this.setState({
      isReady: true,
    });
  };

  private onToggleFullscreen = (e: Event) => {
    const { onFullscreenEnter, onFullscreenExit } = this.props;
    const isFullscreen = e.type === 'fotorama:fullscreenenter';

    const options = this.getOptions(isFullscreen);

    this.fotoramaAPI.setOptions(options);

    if (isFullscreen && onFullscreenEnter) {
      onFullscreenEnter();
    }

    if (!isFullscreen && onFullscreenExit) {
      onFullscreenExit();
    }

    this.setState({ isFullscreen });
  };

  private getOptions = (isFullscreen: boolean = this.state.isFullscreen) => {
    const { slides, galleryOptions } = this.props;
    const options = isFullscreen ? galleryOptions.FULLSCREEN : galleryOptions.DEFAULT;
    const nav = isFullscreen ? 'thumbs' : options.nav;

    return {
      ...options,
      nav: slides.length < 2 ? 'false' : nav,
    };
  };
}
